import {
  ActionIcon,
  Badge,
  Divider,
  Group,
  Indicator,
  Menu,
  ScrollArea,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from "@mantine/core";
import {
  IconBell,
  IconCheckbox,
  IconClipboardCheck,
  IconFlask,
  IconShieldCheck,
  IconUserPlus,
} from "@/lib/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { formatDateTime } from "@/lib/format";
import {
  type NotificationRead,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/lib/api/notifications";

const KIND_ICONS: Record<
  string,
  React.ComponentType<{ size?: number; strokeWidth?: number }>
> = {
  "patient.created": IconUserPlus,
  "exam.assigned": IconClipboardCheck,
  "exam.ready_for_chief": IconShieldCheck,
  "verdict.issued": IconShieldCheck,
  "lab.result_ready": IconFlask,
};

const KIND_COLORS: Record<string, string> = {
  "patient.created": "brand",
  "exam.assigned": "orange",
  "exam.ready_for_chief": "red",
  "verdict.issued": "brand",
  "lab.result_ready": "blue",
};

export function NotificationsMenu() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data } = useQuery({
    queryKey: ["notifications"],
    queryFn: () => listNotifications(false, 25),
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });

  const readMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const readAllMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["notifications"] }),
  });

  const items = data?.items ?? [];
  const unread = data?.unread ?? 0;

  const onClick = (n: NotificationRead) => {
    if (!n.is_read) readMutation.mutate(n.id);
    if (n.link) navigate(n.link);
  };

  const renderTitle = (n: NotificationRead) =>
    t(`notifications.kinds.${n.kind}`, { defaultValue: n.title });

  return (
    <Menu
      position="bottom-end"
      withArrow
      shadow="md"
      radius="md"
      width={380}
      closeOnItemClick={false}
    >
      <Menu.Target>
        <ActionIcon variant="subtle" color="gray" size="lg" radius="xl">
          <Indicator
            color="red"
            label={unread > 99 ? "99+" : unread || undefined}
            size={unread > 0 ? 16 : 8}
            disabled={unread === 0}
            offset={3}
          >
            <IconBell size={20} strokeWidth={1.6} />
          </Indicator>
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown p={0}>
        <Group justify="space-between" px="md" py="sm">
          <Group gap="xs">
            <Text fw={700}>{t("notifications.title")}</Text>
            {unread > 0 && (
              <Badge size="sm" color="red" variant="light" radius="xl">
                {unread}
              </Badge>
            )}
          </Group>
          {unread > 0 && (
            <UnstyledButton
              onClick={() => readAllMutation.mutate()}
              style={{
                fontSize: 12,
                color: "var(--mantine-color-brand-7)",
                fontWeight: 600,
              }}
            >
              {t("notifications.mark_all_read")}
            </UnstyledButton>
          )}
        </Group>
        <Divider />
        <ScrollArea.Autosize mah={420} type="hover">
          {items.length === 0 ? (
            <Stack p="xl" gap={8} align="center">
              <ThemeIcon size={48} radius="xl" variant="light" color="gray">
                <IconCheckbox size={24} strokeWidth={1.6} />
              </ThemeIcon>
              <Text size="sm" c="dimmed" ta="center">
                {t("notifications.empty")}
              </Text>
            </Stack>
          ) : (
            <Stack gap={0}>
              {items.map((n) => {
                const Icon = KIND_ICONS[n.kind] ?? IconBell;
                const color = KIND_COLORS[n.kind] ?? "gray";
                return (
                  <UnstyledButton
                    key={n.id}
                    onClick={() => onClick(n)}
                    style={{
                      padding: "10px 14px",
                      transition: "background 120ms ease",
                      background: n.is_read
                        ? "transparent"
                        : "var(--mantine-color-brand-0)",
                      borderBottom: "1px solid var(--mantine-color-gray-1)",
                    }}
                  >
                    <Group gap="sm" wrap="nowrap" align="flex-start">
                      <ThemeIcon size={36} radius="xl" variant="light" color={color}>
                        <Icon size={18} strokeWidth={2} />
                      </ThemeIcon>
                      <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                        <Group gap="xs" justify="space-between" wrap="nowrap">
                          <Text size="sm" fw={n.is_read ? 500 : 700} truncate>
                            {renderTitle(n)}
                          </Text>
                          {!n.is_read && (
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                background: "var(--mantine-color-red-6)",
                                flexShrink: 0,
                              }}
                            />
                          )}
                        </Group>
                        {n.body && (
                          <Text size="xs" c="dimmed" lineClamp={2}>
                            {n.body}
                          </Text>
                        )}
                        <Text size="xs" c="dimmed">
                          {formatDateTime(n.created_at)}
                        </Text>
                      </Stack>
                    </Group>
                  </UnstyledButton>
                );
              })}
            </Stack>
          )}
        </ScrollArea.Autosize>
      </Menu.Dropdown>
    </Menu>
  );
}
